<script setup lang="ts">
// Keep initial rendering fast with lightweight startup presentation and minimal shared UI.
// Post-startup auxiliary UI belongs in MainApp's lazy-loaded AppAuxiliaryUi.
import { computed, type ShallowRef } from 'vue';
import GlobalDialogHost from '@/components/GlobalDialogHost.vue';
import ToastContainer from '@/components/ToastContainer.vue';
import StartupErrorView from '@/components/startup/StartupErrorView.vue';
import { provideAppPresentation } from '@/composables/useAppPresentation';
import type { StartupState } from '@/logic/startup/types';
import { provideAppEntry } from '@/composables/useAppEntry';
import OnBoardingModalV2 from '@/components/OnBoardingModalV2.vue';

const props = defineProps<{
  startupState: ShallowRef<StartupState>,
}>();

const startup = computed(() => props.startupState.value);
const entry = provideAppEntry({ startupState: props.startupState });
const {
  appInteraction,
} = provideAppPresentation({ startupState: props.startupState, modelSelectionVisible: computed(() => entry.modelSelection.value !== undefined || entry.notFoundRecovery.value !== undefined) });

const appContentInert = computed(() => {
  const interaction = appInteraction.value;
  switch (interaction) {
  case 'blocked-by-startup':
  case 'blocked-by-model-selection':
    return true;
  case 'enabled':
    return undefined;
  default: {
    const _ex: never = interaction;
    return _ex;
  }
  }
});

const appContentAriaHidden = computed(() => appContentInert.value === true
  ? 'true'
  : undefined);

const earlyEntry = computed(() => {
  const state = startup.value;
  switch (state.kind) {
  case 'starting-main':
  case 'rendering-main':
    return state.entrySurface;
  case 'initializing-foundation':
  case 'ready':
  case 'foundation-failed':
  case 'main-failed':
    return undefined;
  default: {
    const _ex: never = state;
    return _ex;
  }
  }
});


defineExpose({
  ...((__BUILD_MODE_IS_TEST__ && {
    TEST_ONLY: {
      // Export internal state and logic used only for testing here. Do not reference these in production logic.
      // ESLint-required for defineExpose.
    },
  }) || {}),
});
</script>

<template>
  <div
    data-testid="app-content-host"
    :inert="appContentInert"
    :aria-hidden="appContentAriaHidden"
  >
    <component
      :is="startup.mainApp"
      v-if="startup.kind === 'rendering-main' || startup.kind === 'ready'"
    />

    <div v-else-if="earlyEntry" tw-class="h-dvh">
      <component :is="earlyEntry" />
    </div>

    <StartupErrorView
      v-else-if="startup.kind === 'foundation-failed' || startup.kind === 'main-failed'"
      :error="startup.error"
    />

    <div
      v-else
      data-testid="startup-background"
      tw-class="h-dvh bg-gray-50 dark:bg-gray-950"
    />
  </div>

  <!-- Keep one synchronous OnBoardingModalV2 outside MainApp across the startup handoff. -->
  <OnBoardingModalV2 v-if="entry.modelSelection.value !== undefined" />
  <GlobalDialogHost />
  <ToastContainer />
</template>
